import type { DlpLabelModel } from 'services/DlpConfiguration/DlpConfigurationService';
import { excelCellText, MAX_DLP_IMPORT_BYTES, readExcelWorkbook, SpreadsheetReadError } from './dlpImportFile';
import { LabelHierarchyIssue, validateDlpLabels } from './dlpLabelHierarchy';

export const MAX_LABEL_IMPORT_ROWS = 10000;
export type LabelImportErrorCode = 'unsupportedFile' | 'fileTooLarge' | 'tooManyLabels' | 'emptyFile' | 'invalidFile' | 'invalidCell' | 'unsafeId' | 'columns' | 'hierarchy' | 'projectRequired' | 'changed';
export class LabelImportError extends Error {
    constructor(public readonly code: LabelImportErrorCode, public readonly issues: (LabelHierarchyIssue & { row: number })[] = []) {
        super(code);
        this.name = 'LabelImportError';
    }
}

const headerKey = (value: string) => {
    const key = value.normalize('NFKC').trim().toLowerCase().replace(/[\s-]+/g, '_');
    return ({ id: 'label_id', name: 'label_name', parent_id: 'parent_label_id' } as Record<string, string>)[key] ?? key;
};
const REQUIRED = ['label_id', 'label_name', 'parent_label_id'];

export async function readLabelsFromFile(file: File, projectId?: string): Promise<DlpLabelModel[]> {
    if (file.name.split('.').pop()?.toLowerCase() !== 'xlsx') throw new LabelImportError('unsupportedFile');
    if (file.size > MAX_DLP_IMPORT_BYTES) throw new LabelImportError('fileTooLarge');
    try {
        const workbook = await readExcelWorkbook(file);
        // The configuration export includes a dedicated import-friendly label sheet.
        const sheet = workbook.worksheets.find(s => s.name.toLowerCase() === 'dlp_labels') ?? workbook.worksheets[0];
        if (!sheet) throw new LabelImportError('emptyFile');
        if (sheet.actualRowCount > MAX_LABEL_IMPORT_ROWS + 1) throw new LabelImportError('tooManyLabels');
        let columns: Map<string, number> | undefined;
        const labels: DlpLabelModel[] = [];
        const sourceRows: number[] = [];
        sheet.eachRow((row, rowNumber) => {
            if (!columns) {
                const header = new Map<string, number>();
                row.eachCell((cell, index) => {
                    const key = headerKey(excelCellText(cell.value));
                    if (!key) return;
                    if (header.has(key)) throw new LabelImportError('columns');
                    header.set(key, index);
                });
                if (!header.size) return;
                if (!REQUIRED.every(key => header.has(key))) throw new LabelImportError('columns');
                if (header.has('project_id') && projectId === undefined) throw new LabelImportError('projectRequired');
                columns = header;
                return;
            }
            if (columns.has('project_id') && excelCellText(row.getCell(columns.get('project_id')!).value, true).trim() !== projectId) return;
            const id = excelCellText(row.getCell(columns.get('label_id')!).value, true).trim();
            const name = excelCellText(row.getCell(columns.get('label_name')!).value).trim();
            const parent = excelCellText(row.getCell(columns.get('parent_label_id')!).value, true).trim();
            if (!id && !name && !parent) return;
            labels.push({ id, name, parent_id: parent || null });
            sourceRows.push(rowNumber);
        });
        if (!labels.length) throw new LabelImportError('emptyFile');
        if (labels.length > MAX_LABEL_IMPORT_ROWS) throw new LabelImportError('tooManyLabels');
        const issues = validateDlpLabels(labels);
        if (issues.length) throw new LabelImportError('hierarchy', issues.map(issue => ({ ...issue, row: sourceRows[issue.index] })));
        return labels;
    } catch (error) {
        if (error instanceof SpreadsheetReadError) throw new LabelImportError(error.code);
        throw error;
    }
}
